import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@iptv/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle2,
  CreditCard,
  ExternalLink,
  Eye,
  FileText,
  Globe,
  MessageSquare,
  Phone,
  Search,
  ShoppingCart,
  Tv,
  UserPlus,
  Wallet,
} from 'lucide-react';
import { useState } from 'react';

import { adminFetch } from '../lib/api';
import { SortableTh, useTableSort } from '../lib/useTableSort';

interface TimelineEntry {
  kind:
    | 'lead_created'
    | 'traffic_event'
    | 'order_created'
    | 'order_status_changed'
    | 'payment_event'
    | 'subscription_created'
    | 'provision_attempt'
    | 'whatsapp_message';
  at: string;
  title: string;
  subtitle: string | null;
  target_id: string | null;
  target_type: string | null;
  metadata: Record<string, unknown>;
}

interface LeadRow {
  id: number;
  email: string;
  name: string;
  whatsapp: string;
  country_iso: string;
  selected_bouquets: string[];
  site_source_id: number | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  consent_marketing: boolean;
  comment: string | null;
  created_at: string;
  // IPTV linkage — backend populates these from the most-recent
  // Subscription row for this lead. Stays null when the lead never
  // reached successful provisioning (no order paid yet, fitness
  // vertical, manual order).
  iptv_username: string | null;
  iptv_provider: string | null;
  iptv_status: string | null;
}

interface LeadSubscription {
  id: number;
  order_id: number;
  order_ref: string | null;
  iptv_username: string | null;
  iptv_provider: string | null;
  iptv_panel_url: string | null;
  connections: number;
  status: string;
  started_at: string | null;
  expires_at: string;
  vertical: string;
}

/**
 * Friendly labels for bouquet codes. The DB stores the raw codes as a free-
 * form text[], so both the current set (post-2026-05-16 rework) and the
 * legacy set still live in the table — we label both. The `(legacy)`
 * suffix is intentional: it lets the operator distinguish historical rows
 * from current submissions when bouquet-driven business decisions diverge.
 */
const BOUQUET_LABELS: Record<string, string> = {
  // New codes
  worldwide: 'Worldwide',
  us: 'US',
  latino: 'Latino',
  canadian: 'Canadian',
  world_cup: 'World Cup',
  local: 'Local channels',
  // Legacy codes (kept for historical rows)
  global: 'Global (legacy)',
  'us-sports': 'US sports (legacy)',
  kids: 'Kids (legacy)',
  wc26: 'World Cup 2026 (legacy)',
  hispano: 'Hispano (legacy)',
};

function formatBouquets(codes: string[]): string {
  if (!codes.length) return '—';
  return codes.map((c) => BOUQUET_LABELS[c] ?? c).join(', ');
}

interface LeadOrder {
  id: number;
  order_ref: string;
  status: string;
  total_cents: number;
  currency: string;
  created_at: string;
  paid_at: string | null;
}

interface LeadDetail extends LeadRow {
  orders: LeadOrder[];
  subscriptions: LeadSubscription[];
}

interface SiteRef {
  id: number;
  slug: string;
}

function downloadCsv(rows: LeadRow[], sites: SiteRef[]) {
  const slug = (id: number | null) => sites.find((s) => s.id === id)?.slug ?? '';
  const header = [
    'id',
    'email',
    'name',
    'whatsapp',
    'country',
    'site',
    'bouquets',
    'comment',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'consent',
    'created_at',
    // IPTV linkage from the lead's most recent subscription. Exported
    // so operators can grep the CSV for a known username or filter
    // by provider when triaging support tickets in bulk.
    'iptv_username',
    'iptv_provider',
    'iptv_status',
  ];
  const escape = (s: unknown) => {
    const v = String(s ?? '');
    return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
  };
  const csv = [
    header.join(','),
    ...rows.map((r) =>
      [
        r.id,
        r.email,
        r.name,
        r.whatsapp,
        r.country_iso,
        slug(r.site_source_id),
        r.selected_bouquets.join('|'),
        r.comment ?? '',
        r.utm_source ?? '',
        r.utm_medium ?? '',
        r.utm_campaign ?? '',
        r.consent_marketing ? 'yes' : 'no',
        r.created_at,
        r.iptv_username ?? '',
        r.iptv_provider ?? '',
        r.iptv_status ?? '',
      ]
        .map(escape)
        .join(','),
    ),
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function LeadsPage() {
  const [search, setSearch] = useState('');
  const [siteId, setSiteId] = useState<string>('');
  // "Comments only" filter turns the table into a triage queue — only
  // leads where the customer typed something in the optional comment
  // textarea on the checkout form. Implemented server-side via the
  // `has_comment=true` query param so pagination still works.
  const [commentsOnly, setCommentsOnly] = useState(false);
  const [openLeadId, setOpenLeadId] = useState<number | null>(null);

  const sites = useQuery({
    queryKey: ['admin', 'sites'],
    queryFn: () => adminFetch<SiteRef[]>('/api/v1/admin/sites'),
  });

  const leads = useQuery<LeadRow[]>({
    queryKey: ['admin', 'leads', search, siteId, commentsOnly],
    queryFn: () =>
      adminFetch<LeadRow[]>('/api/v1/admin/leads', {
        query: {
          search: search.length >= 2 ? search : undefined,
          site_source_id: siteId || undefined,
          has_comment: commentsOnly ? true : undefined,
          limit: 200,
        },
      }),
  });

  const detail = useQuery({
    queryKey: ['admin', 'leads', openLeadId],
    queryFn: () => adminFetch<LeadDetail>(`/api/v1/admin/leads/${openLeadId}`),
    enabled: openLeadId !== null,
  });

  // Activity-timeline query — only fires when a lead dialog is open. The
  // dialog renders <ActivityTimeline> below the recap card.
  const timeline = useQuery({
    queryKey: ['admin', 'leads', openLeadId, 'timeline'],
    queryFn: () =>
      adminFetch<TimelineEntry[]>(`/api/v1/admin/leads/${openLeadId}/timeline`, {
        query: { limit: 200 },
      }),
    enabled: openLeadId !== null,
  });

  const queryClient = useQueryClient();
  const markPaid = useMutation({
    mutationFn: (orderId: number) =>
      adminFetch(`/api/v1/admin/orders/${orderId}/mark-paid`, {
        method: 'POST',
        body: { note: 'Marked paid from lead dialog' },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'leads', openLeadId] });
      queryClient.invalidateQueries({
        queryKey: ['admin', 'leads', openLeadId, 'timeline'],
      });
    },
  });

  const { sorted, sortKey, sortDir, toggleSort } = useTableSort<LeadRow>(leads.data, {
    initialKey: 'created_at',
    initialDir: 'desc',
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search email, name, whatsapp…"
            className="pl-8 w-64"
          />
        </div>
        <select
          value={siteId}
          onChange={(e) => setSiteId(e.target.value)}
          className="rounded-md border bg-background px-2 py-1 text-sm"
        >
          <option value="">All sites</option>
          {sites.data?.map((s) => (
            <option key={s.id} value={s.id}>
              {s.slug}
            </option>
          ))}
        </select>
        <Button
          variant={commentsOnly ? 'default' : 'outline'}
          size="sm"
          onClick={() => setCommentsOnly(!commentsOnly)}
          title="Show only leads where the customer left a comment"
        >
          <MessageSquare className="mr-1.5 h-3.5 w-3.5" />
          Comments only
        </Button>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{leads.data?.length ?? 0} leads</span>
          <Button
            variant="outline"
            disabled={!leads.data?.length}
            onClick={() => leads.data && sites.data && downloadCsv(leads.data, sites.data)}
          >
            Export CSV
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50">
            <tr>
              <SortableTh sortKey="email" current={sortKey} dir={sortDir} onSort={toggleSort}>
                Email
              </SortableTh>
              <SortableTh sortKey="name" current={sortKey} dir={sortDir} onSort={toggleSort}>
                Name
              </SortableTh>
              <SortableTh sortKey="whatsapp" current={sortKey} dir={sortDir} onSort={toggleSort}>
                WhatsApp
              </SortableTh>
              <SortableTh sortKey="country_iso" current={sortKey} dir={sortDir} onSort={toggleSort}>
                Country
              </SortableTh>
              <SortableTh
                sortKey="site_source_id"
                current={sortKey}
                dir={sortDir}
                onSort={toggleSort}
              >
                Site
              </SortableTh>
              <SortableTh sortKey="utm_source" current={sortKey} dir={sortDir} onSort={toggleSort}>
                UTM
              </SortableTh>
              <SortableTh
                sortKey="iptv_username"
                current={sortKey}
                dir={sortDir}
                onSort={toggleSort}
              >
                IPTV ID
              </SortableTh>
              <SortableTh sortKey="created_at" current={sortKey} dir={sortDir} onSort={toggleSort}>
                Created
              </SortableTh>
              <th className="px-3 py-2 text-right text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {leads.isPending && (
              <tr>
                <td colSpan={9} className="px-3 py-4 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {leads.data?.length === 0 && (
              <tr>
                <td colSpan={9} className="px-3 py-4 text-center text-muted-foreground">
                  No leads yet — try the public lead form on a marketing site.
                </td>
              </tr>
            )}
            {sorted.map((l) => (
              <tr key={l.id} className="border-t">
                <td className="px-3 py-2 font-mono text-xs">
                  <span className="inline-flex items-center gap-1.5">
                    {l.email}
                    {l.comment && (
                      <MessageSquare
                        className="h-3 w-3 text-amber-600"
                        aria-label="Customer left a comment"
                      />
                    )}
                  </span>
                </td>
                <td className="px-3 py-2">{l.name}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{l.whatsapp}</td>
                <td className="px-3 py-2">{l.country_iso}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">
                  {sites.data?.find((s) => s.id === l.site_source_id)?.slug ?? '—'}
                </td>
                <td className="px-3 py-2 text-xs text-muted-foreground">
                  {[l.utm_source, l.utm_medium, l.utm_campaign].filter(Boolean).join(' · ') || '—'}
                </td>
                <td className="px-3 py-2 font-mono text-[11px]">
                  {l.iptv_username ? (
                    <span
                      className="inline-flex items-center gap-1"
                      title={
                        l.iptv_provider
                          ? `provider: ${l.iptv_provider} · status: ${l.iptv_status ?? '—'}`
                          : undefined
                      }
                    >
                      <Tv className="h-3 w-3 text-indigo-600" />
                      {l.iptv_username}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs">{new Date(l.created_at).toLocaleString()}</td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="ghost" onClick={() => setOpenLeadId(l.id)}>
                    <Eye className="h-3.5 w-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={openLeadId !== null} onOpenChange={(o) => !o && setOpenLeadId(null)}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Lead detail</DialogTitle>
          </DialogHeader>
          {detail.isPending && <p className="text-sm text-muted-foreground">Loading…</p>}
          {detail.data && (
            <div className="space-y-4">
              {/* Quick-action toolbar: a WhatsApp deep-link prefilled with
                  the lead's email + name, so support can open a chat with
                  one click. Country dialing is up to the operator to
                  format; we just open wa.me/<digits>. */}
              <div className="flex flex-wrap items-center gap-2">
                <a
                  href={`https://wa.me/${(detail.data.whatsapp || '').replace(/\D/g, '')}?text=${encodeURIComponent(
                    `Hi ${detail.data.name}, this is the IPTV support team about your subscription.`,
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700"
                >
                  <Phone className="h-3.5 w-3.5" /> WhatsApp
                </a>
                <a
                  href={`mailto:${detail.data.email}`}
                  className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs hover:bg-secondary"
                >
                  <FileText className="h-3.5 w-3.5" /> Email
                </a>
              </div>

              <div className="grid gap-2 text-sm md:grid-cols-2">
                <Field label="Email" value={detail.data.email} />
                <Field label="Name" value={detail.data.name} />
                <Field label="WhatsApp" value={detail.data.whatsapp} />
                <Field label="Country" value={detail.data.country_iso} />
                <Field label="Bouquets" value={formatBouquets(detail.data.selected_bouquets)} />
                <Field
                  label="Marketing consent"
                  value={detail.data.consent_marketing ? 'yes' : 'no'}
                />
                <Field
                  label="UTM"
                  value={
                    [detail.data.utm_source, detail.data.utm_medium, detail.data.utm_campaign]
                      .filter(Boolean)
                      .join(' · ') || '—'
                  }
                />
                <Field label="Created" value={new Date(detail.data.created_at).toLocaleString()} />
              </div>

              {detail.data.comment && (
                <div className="rounded-md border-l-4 border-amber-500 bg-amber-50 p-3 dark:bg-amber-950/20">
                  <div className="mb-1 flex items-center gap-1.5 text-xs uppercase tracking-wider text-amber-800 dark:text-amber-300">
                    <MessageSquare className="h-3.5 w-3.5" />
                    Customer comment
                  </div>
                  <p className="text-sm">{detail.data.comment}</p>
                </div>
              )}

              <div>
                <Label className="mb-2 block text-xs uppercase tracking-wider text-muted-foreground">
                  Orders ({detail.data.orders.length})
                </Label>
                {detail.data.orders.length === 0 && (
                  <p className="text-sm text-muted-foreground">No orders yet.</p>
                )}
                <ul className="space-y-2">
                  {detail.data.orders.map((o) => (
                    <li
                      key={o.id}
                      className="flex flex-wrap items-center gap-3 rounded-md border px-3 py-2 text-sm"
                    >
                      <span className="font-mono text-xs">{o.order_ref}</span>
                      <Badge
                        variant={
                          o.status === 'paid'
                            ? 'success'
                            : o.status === 'awaiting_payment'
                              ? 'warning'
                              : 'secondary'
                        }
                      >
                        {o.status}
                      </Badge>
                      <span className="font-medium">
                        {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: o.currency,
                        }).format(o.total_cents / 100)}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(o.created_at).toLocaleDateString()}
                      </span>
                      <div className="ml-auto flex items-center gap-1.5">
                        {o.status !== 'paid' && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={markPaid.isPending}
                            onClick={() => markPaid.mutate(o.id)}
                            title="Mark this order as paid (audit-logged)"
                          >
                            <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
                            Mark paid
                          </Button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              {/* IPTV access — surfaces the identifier(s) we provisioned
                  for this customer so operators can paste the username
                  into a support ticket or open the line on the
                  provider's panel in one click. One row per
                  subscription (renewals produce multiple). Empty when
                  no order has reached successful provisioning yet. */}
              <div>
                <Label className="mb-2 block text-xs uppercase tracking-wider text-muted-foreground">
                  IPTV access ({detail.data.subscriptions.length})
                </Label>
                {detail.data.subscriptions.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    No IPTV line provisioned for this customer yet.
                  </p>
                )}
                <ul className="space-y-2">
                  {detail.data.subscriptions.map((s) => (
                    <li
                      key={s.id}
                      className="flex flex-wrap items-center gap-3 rounded-md border px-3 py-2 text-sm"
                    >
                      <Tv className="h-3.5 w-3.5 text-indigo-600" />
                      <span className="font-mono text-xs">{s.iptv_username ?? '—'}</span>
                      {s.iptv_provider && (
                        <Badge variant="secondary" className="text-[10px] uppercase">
                          {s.iptv_provider}
                        </Badge>
                      )}
                      <Badge
                        variant={
                          s.status === 'active'
                            ? 'success'
                            : s.status === 'expired'
                              ? 'destructive'
                              : 'secondary'
                        }
                      >
                        {s.status}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {s.connections} conn · expires {new Date(s.expires_at).toLocaleDateString()}
                      </span>
                      {s.order_ref && (
                        <span className="text-[11px] text-muted-foreground">
                          order <span className="font-mono">{s.order_ref}</span>
                        </span>
                      )}
                      <div className="ml-auto flex items-center gap-1.5">
                        {s.iptv_panel_url && (
                          <a
                            href={s.iptv_panel_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-blue-600 underline hover:text-blue-800"
                            title="Open provider panel for this line"
                          >
                            <ExternalLink className="h-3 w-3" />
                            Panel
                          </a>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Unified activity timeline. Rendered as a flat
                  chronological list with a small icon per kind so
                  operators can scan a lead's history at a glance. */}
              <div>
                <Label className="mb-2 block text-xs uppercase tracking-wider text-muted-foreground">
                  Activity
                </Label>
                <ActivityTimeline entries={timeline.data ?? []} pending={timeline.isPending} />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const TIMELINE_ICONS: Record<TimelineEntry['kind'], React.ReactNode> = {
  lead_created: <UserPlus className="h-3.5 w-3.5 text-primary" />,
  traffic_event: <Globe className="h-3.5 w-3.5 text-muted-foreground" />,
  order_created: <ShoppingCart className="h-3.5 w-3.5 text-blue-600" />,
  order_status_changed: <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />,
  payment_event: <Wallet className="h-3.5 w-3.5 text-purple-600" />,
  subscription_created: <CreditCard className="h-3.5 w-3.5 text-indigo-600" />,
  provision_attempt: <ExternalLink className="h-3.5 w-3.5 text-amber-600" />,
  whatsapp_message: <Phone className="h-3.5 w-3.5 text-emerald-600" />,
};

function ActivityTimeline({ entries, pending }: { entries: TimelineEntry[]; pending: boolean }) {
  if (pending) {
    return <p className="text-sm text-muted-foreground">Loading activity…</p>;
  }
  if (!entries.length) {
    return <p className="text-sm text-muted-foreground">No activity recorded.</p>;
  }
  return (
    <ol className="relative space-y-3 border-l border-border pl-5">
      {entries.map((e, i) => (
        <li key={`${e.kind}-${e.target_id ?? i}-${e.at}`} className="relative">
          <span className="absolute -left-[1.4rem] top-1 flex h-5 w-5 items-center justify-center rounded-full border bg-background">
            {TIMELINE_ICONS[e.kind]}
          </span>
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-sm">{e.title}</span>
            <span className="text-[11px] text-muted-foreground">
              {new Date(e.at).toLocaleString()}
            </span>
          </div>
          {e.subtitle && <p className="text-xs text-muted-foreground">{e.subtitle}</p>}
        </li>
      ))}
    </ol>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div>{value}</div>
    </div>
  );
}
